import React, { useEffect, useRef, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Image,
  Input,
  List,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  UnlockOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { formatDateTime, formatDay, formatEuro } from "../workReports/format";
import { shrinkPhoto } from "../workReports/photos";
import {
  acceptancePdfUrl,
  createSignLink,
  defectPhotoUrl,
  deleteAcceptance,
  deleteDefectPhoto,
  emailAcceptance,
  getAcceptance,
  recordAcceptance,
  refuseAcceptance,
  reopenAcceptance,
  requestAcceptance,
  requestLetterUrl,
  revokeSignLink,
  signAcceptance,
  updateAcceptance,
  updateDefect,
  uploadDefectPhoto
} from "./api";
import {
  DefectStatusDialog,
  LinkResult,
  RecordDialog,
  RefuseDialog,
  RequestDialog,
  SignOnSiteDialog,
  SimpleTextDialog
} from "./AcceptanceDialogs";
import {
  ACCEPTANCE_COLORS,
  DEFECT_COLORS,
  acceptanceStatusLabels,
  basisLabels,
  categoryLabels,
  defectStatusLabels,
  methodLabels
} from "./labels";
import useMeta from "./useMeta";
import "./warranty.css";

const EDITABLE = ["draft", "requested"];
const ROLES = ["contractor", "customer", "architect", "other"];
const CATEGORIES = [
  "building",
  "maintenance_parts",
  "fire_parts",
  "industrial_fire_parts",
  "other_work"
];

/** The editable part of the protocol, as sent to the server. */
const formOf = a => ({
  date: a.date,
  time: a.time || "",
  place: a.place || "",
  scope: a.scope,
  contractBasis: a.contractBasis,
  workDescription: a.workDescription || "",
  internalNote: a.internalNote || "",
  participants: (a.participants || []).map(p => ({ ...p })),
  defects: (a.defects || []).map(d => ({
    _id: d._id,
    description: d.description,
    location: d.location,
    essential: d.essential,
    deadline: d.deadline,
    responsible: d.responsible
  })),
  reservations: { ...(a.reservations || {}) },
  warrantyCategories: [...(a.warrantyCategories || [])]
});

const AcceptanceEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const [meta] = useMeta();
  const [acceptance, setAcceptance] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [defectDialog, setDefectDialog] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const photoInput = useRef(null);
  const photoTarget = useRef(null);

  const apply = data => {
    setAcceptance(data);
    setForm(formOf(data));
    setDirty(false);
  };

  useEffect(() => {
    getAcceptance(id)
      .then(res => apply(res.data.data))
      .catch(generalErrorAlert);
  }, [id]);

  if (!acceptance || !form || !meta) return <Skeleton active paragraph={{ rows: 12 }} />;

  const editable = EDITABLE.includes(acceptance.status) && meta.can.update;
  const statusLabels = acceptanceStatusLabels(t);
  const defectLabels = defectStatusLabels(t);
  const set = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const setDefect = (index, patch) =>
    set({ defects: form.defects.map((d, i) => (i === index ? { ...d, ...patch } : d)) });
  const setParticipant = (index, patch) =>
    set({ participants: form.participants.map((p, i) => (i === index ? { ...p, ...patch } : p)) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateAcceptance(id, form);
      apply(res.data.data);
      Success(t("Saved"));
      return res.data.data;
    } catch (error) {
      generalErrorAlert(error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /** Saves pending changes first, then runs `fn`; reports the result. */
  const run = async (fn, message) => {
    try {
      if (dirty) await save();
      const res = await fn();
      if (res?.data?.data) apply(res.data.data);
      if (res?.data?.warnings?.includes?.("PDF_NOT_ARCHIVED")) {
        Modal.warning({
          title: t("The PDF could not be archived. It is created again when opened.")
        });
      } else if (message) Success(message);
      setDialog(null);
      return res;
    } catch (error) {
      if (error?.response) generalErrorAlert(error);
      throw error;
    }
  };

  const addPhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !photoTarget.current) return;
    try {
      const { blob } = await shrinkPhoto(file);
      const res = await uploadDefectPhoto(id, photoTarget.current, blob);
      apply(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const draftActions = [
    { key: "link", label: t("Signing link for the customer"), onClick: () => setDialog("link") },
    {
      key: "record",
      label: t("Record acceptance (paper or conduct)"),
      onClick: () => setDialog("record")
    },
    { key: "request", label: t("Acceptance by deadline"), onClick: () => setDialog("request") },
    { key: "refuse", label: t("Acceptance refused"), onClick: () => setDialog("refuse") },
    {
      key: "pdf",
      label: t("Preview PDF"),
      onClick: () => window.open(acceptancePdfUrl(id, lng), "_blank")
    },
    ...(acceptance.status === "draft" && meta.can.delete && !acceptance.warranty
      ? [
          {
            key: "delete",
            danger: true,
            label: t("Delete"),
            onClick: () =>
              Modal.confirm({
                rootClassName: "warranty-page",
                title: t("Delete this acceptance protocol?"),
                okButtonProps: { danger: true },
                okText: t("Delete"),
                cancelText: t("Cancel"),
                onOk: () =>
                  deleteAcceptance(id)
                    .then(() => history.push("/user/warranty?tab=acceptances"))
                    .catch(generalErrorAlert)
              })
          }
        ]
      : [])
  ];

  const customerSigned = acceptance.signatures?.customer;
  const contractorSigned = acceptance.signatures?.contractor;

  return (
    <div className="warranty-page">
      <Prompt when={dirty && !saving} message={t("You have unsaved changes. Leave anyway?")} />
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={addPhoto}
      />

      <Card className="mb-3">
        <div className="wty-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/warranty?tab=acceptances")}
          />
          <div className="wty-header-title">
            <Typography.Title level={4}>
              {t("Acceptance protocol")} {acceptance.number}
            </Typography.Title>
            <Space size={4} wrap>
              <Tag color={ACCEPTANCE_COLORS[acceptance.status]}>
                {statusLabels[acceptance.status]}
              </Tag>
              {acceptance.scope === "partial" && <Tag>{t("Partial acceptance")}</Tag>}
              {acceptance.deemed && <Tag>{t("Fictitious")}</Tag>}
            </Space>
          </div>
          <Space wrap className="wty-header-actions">
            {editable && (
              <>
                <Button
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => save().catch(() => {})}
                >
                  {t("Save")}
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={async () => {
                    try {
                      if (dirty) await save();
                      setDialog("sign");
                    } catch {
                      // shown by save
                    }
                  }}
                >
                  {t("Sign now")}
                </Button>
                <Dropdown menu={{ items: draftActions }} trigger={["click"]}>
                  <Button icon={<MoreOutlined />} aria-label={t("More")} />
                </Dropdown>
              </>
            )}
            {!EDITABLE.includes(acceptance.status) && (
              <>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={() => window.open(acceptancePdfUrl(id, lng), "_blank")}
                >
                  PDF
                </Button>
                <Button icon={<MailOutlined />} onClick={() => setDialog("email")}>
                  {t("Email")}
                </Button>
                {meta.can.update && (
                  <Button icon={<UnlockOutlined />} onClick={() => setDialog("reopen")}>
                    {t("Reopen")}
                  </Button>
                )}
              </>
            )}
            {acceptance.status === "requested" && meta.can.update && (
              <Button icon={<UnlockOutlined />} onClick={() => setDialog("reopen")}>
                {t("Stop the deadline")}
              </Button>
            )}
          </Space>
        </div>

        {acceptance.status === "requested" && (
          <Alert
            type="info"
            showIcon
            className="mt-2"
            message={t("Deemed accepted after {{date}} unless refused", {
              date: formatDay(acceptance.fiction?.deemedDate)
            })}
            description={t(
              'If the customer refuses naming defects, record it with "Acceptance refused".'
            )}
            action={
              acceptance.fiction?.letter && (
                <Button
                  size="small"
                  href={requestLetterUrl(id)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("Letter")}
                </Button>
              )
            }
          />
        )}
        {acceptance.status === "accepted" && (
          <Alert
            type="success"
            showIcon
            className="mt-2"
            message={t("Accepted on {{date}}. The warranty starts with this day.", {
              date: formatDay(acceptance.acceptedOn)
            })}
            action={
              acceptance.warranty && (
                <Link to={`/user/warranty/warranties/${acceptance.warranty}`}>{t("Warranty")}</Link>
              )
            }
          />
        )}
        {acceptance.status === "refused" && (
          <Alert
            type="error"
            showIcon
            className="mt-2"
            message={t("Acceptance refused on {{date}}", {
              date: formatDay(acceptance.refusal?.on)
            })}
            description={acceptance.refusal?.reason}
          />
        )}
        {editable && acceptance.signLink?.active && (
          <Alert
            type="info"
            showIcon
            className="mt-2"
            message={t("A signing link is active until {{date}}.", {
              date: formatDateTime(acceptance.signLink.expiresAt, lng)
            })}
            action={
              <Button size="small" onClick={() => run(() => revokeSignLink(id))}>
                {t("Revoke")}
              </Button>
            }
          />
        )}
        {editable && contractorSigned && !customerSigned && (
          <Alert
            type="success"
            className="mt-2"
            message={t("Signed for the contractor by {{name}}.", { name: contractorSigned.name })}
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={t("Protocol")} className="mb-3">
            <Row gutter={[12, 12]}>
              <Col xs={12} md={8}>
                <div className="wty-label">{t("Date")}</div>
                <DatePicker
                  className="wty-full"
                  format="DD.MM.YYYY"
                  allowClear={false}
                  disabled={!editable}
                  value={form.date ? dayjs(form.date) : null}
                  onChange={value => set({ date: value ? value.format("YYYY-MM-DD") : form.date })}
                />
              </Col>
              <Col xs={12} md={4}>
                <div className="wty-label">{t("Time")}</div>
                <Input
                  disabled={!editable}
                  placeholder="10:00"
                  value={form.time}
                  maxLength={5}
                  onChange={e => set({ time: e.target.value })}
                />
              </Col>
              <Col xs={24} md={12}>
                <div className="wty-label">{t("Place")}</div>
                <Input
                  disabled={!editable}
                  value={form.place}
                  onChange={e => set({ place: e.target.value })}
                />
              </Col>
              <Col xs={24} md={12}>
                <div className="wty-label">{t("Contract basis")}</div>
                <Select
                  className="wty-full"
                  disabled={!editable}
                  value={form.contractBasis}
                  onChange={value => set({ contractBasis: value })}
                  options={Object.entries(basisLabels(t)).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Col>
              <Col xs={24} md={12}>
                <div className="wty-label">{t("Scope")}</div>
                <Select
                  className="wty-full"
                  disabled={!editable}
                  value={form.scope}
                  onChange={value => set({ scope: value })}
                  options={[
                    { value: "full", label: t("Entire work") },
                    { value: "partial", label: t("Partial acceptance") }
                  ]}
                />
              </Col>
              <Col xs={24}>
                <div className="wty-small">
                  {[
                    acceptance.projectInfo?.name,
                    acceptance.clientInfo?.name,
                    acceptance.offerInfo &&
                      `${acceptance.offerInfo.number} ${acceptance.offerInfo.title}`
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  {!EDITABLE.includes(acceptance.status) &&
                    ` · ${methodLabels(t)[acceptance.method]}`}
                </div>
              </Col>
              <Col xs={24}>
                <div className="wty-label">{t("Accepted work")}</div>
                <Input.TextArea
                  disabled={!editable}
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  maxLength={10000}
                  value={form.workDescription}
                  onChange={e => set({ workDescription: e.target.value })}
                />
              </Col>
            </Row>
          </Card>

          <Card
            title={t("Defects")}
            className="mb-3"
            extra={
              editable && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    set({
                      defects: [
                        ...form.defects,
                        {
                          description: "",
                          location: "",
                          essential: false,
                          deadline: "",
                          responsible: ""
                        }
                      ]
                    })
                  }
                >
                  {t("Add defect")}
                </Button>
              )
            }
          >
            {!form.defects.length ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No defects found")} />
            ) : (
              <List
                dataSource={form.defects}
                renderItem={(d, index) => {
                  const stored = acceptance.defects.find(x => x._id === d._id);
                  return (
                    <List.Item className="wty-defect">
                      <div className="wty-full">
                        <Space className="wty-defect-head" wrap>
                          <Typography.Text strong>{index + 1}.</Typography.Text>
                          {stored && (
                            <Tag color={DEFECT_COLORS[stored.status]}>
                              {defectLabels[stored.status]}
                            </Tag>
                          )}
                          {d.essential && <Tag color="error">{t("Essential")}</Tag>}
                        </Space>
                        {editable ? (
                          <Row gutter={[8, 8]}>
                            <Col xs={24}>
                              <Input.TextArea
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                placeholder={t("Description")}
                                value={d.description}
                                maxLength={2000}
                                onChange={e => setDefect(index, { description: e.target.value })}
                              />
                            </Col>
                            <Col xs={24} md={8}>
                              <Input
                                placeholder={t("Location or part")}
                                value={d.location}
                                onChange={e => setDefect(index, { location: e.target.value })}
                              />
                            </Col>
                            <Col xs={12} md={6}>
                              <DatePicker
                                className="wty-full"
                                format="DD.MM.YYYY"
                                placeholder={t("Deadline")}
                                value={d.deadline ? dayjs(d.deadline) : null}
                                onChange={value =>
                                  setDefect(index, {
                                    deadline: value ? value.format("YYYY-MM-DD") : ""
                                  })
                                }
                              />
                            </Col>
                            <Col xs={12} md={6}>
                              <Select
                                className="wty-full"
                                showSearch
                                allowClear
                                placeholder={t("Responsible")}
                                value={d.responsible || undefined}
                                onChange={value => setDefect(index, { responsible: value || "" })}
                                options={(meta.employees || []).map(e => ({
                                  value: e.name,
                                  label: e.name
                                }))}
                              />
                            </Col>
                            <Col xs={24} md={4}>
                              <Space>
                                <Switch
                                  size="small"
                                  checked={d.essential}
                                  onChange={value => setDefect(index, { essential: value })}
                                />
                                <span className="wty-small">{t("Essential")}</span>
                              </Space>
                            </Col>
                          </Row>
                        ) : (
                          <>
                            <div className="wty-pre">{d.description}</div>
                            <div className="wty-small">
                              {[
                                d.location,
                                d.deadline && t("until {{date}}", { date: formatDay(d.deadline) }),
                                d.responsible
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                            {stored?.fixedOn && (
                              <div className="wty-small">
                                {t("Fixed on {{date}}", { date: formatDay(stored.fixedOn) })}
                                {stored.fixNote ? `: ${stored.fixNote}` : ""}
                              </div>
                            )}
                            {stored?.confirmedOn && (
                              <div className="wty-small">
                                {t("Confirmed by {{name}} on {{date}}", {
                                  name: stored.confirmedByName,
                                  date: formatDay(stored.confirmedOn)
                                })}
                              </div>
                            )}
                          </>
                        )}
                        {stored?.photos?.length > 0 && (
                          <Image.PreviewGroup>
                            <Space wrap className="wty-photos">
                              {stored.photos.map(p => (
                                <div key={p._id} className="wty-photo">
                                  <Image
                                    width={88}
                                    height={88}
                                    src={defectPhotoUrl(id, p._id)}
                                    alt={p.caption || ""}
                                  />
                                  {editable && (
                                    <Button
                                      size="small"
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      aria-label={t("Remove photo")}
                                      onClick={() => run(() => deleteDefectPhoto(id, d._id, p._id))}
                                    />
                                  )}
                                </div>
                              ))}
                            </Space>
                          </Image.PreviewGroup>
                        )}
                        <Space wrap className="mt-2">
                          {editable && stored && (
                            <Button
                              size="small"
                              icon={<CameraOutlined />}
                              onClick={() => {
                                photoTarget.current = d._id;
                                photoInput.current?.click();
                              }}
                            >
                              {t("Photo")}
                            </Button>
                          )}
                          {editable && !stored && (
                            <span className="wty-small">{t("Save to add photos.")}</span>
                          )}
                          {editable && (
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                set({ defects: form.defects.filter((_, i) => i !== index) })
                              }
                            >
                              {t("Remove")}
                            </Button>
                          )}
                          {!editable && meta.can.update && stored?.status === "open" && (
                            <Button
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={() => setDefectDialog({ defect: stored, status: "fixed" })}
                            >
                              {t("Mark as fixed")}
                            </Button>
                          )}
                          {!editable &&
                            meta.can.update &&
                            stored &&
                            stored.status !== "accepted" && (
                              <Button
                                size="small"
                                type="primary"
                                onClick={() =>
                                  setDefectDialog({ defect: stored, status: "accepted" })
                                }
                              >
                                {t("Customer confirmed the fix")}
                              </Button>
                            )}
                          {!editable && meta.can.update && stored && stored.status !== "open" && (
                            <Button
                              size="small"
                              type="text"
                              onClick={() =>
                                run(() => updateDefect(id, stored._id, { status: "open" }))
                              }
                            >
                              {t("Open again")}
                            </Button>
                          )}
                        </Space>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title={t("Participants")} className="mb-3">
            {form.participants.map((p, index) => (
              <Row gutter={[8, 8]} key={index} className="mb-2">
                <Col xs={24} md={9}>
                  <Input
                    disabled={!editable}
                    placeholder={t("Name")}
                    value={p.name}
                    onChange={e => setParticipant(index, { name: e.target.value })}
                  />
                </Col>
                <Col xs={12} md={7}>
                  <Select
                    className="wty-full"
                    disabled={!editable}
                    value={p.role}
                    onChange={value => setParticipant(index, { role: value })}
                    options={ROLES.map(r => ({
                      value: r,
                      label: {
                        contractor: t("Contractor"),
                        customer: t("Customer"),
                        architect: t("Architect / site manager"),
                        other: t("Other")
                      }[r]
                    }))}
                  />
                </Col>
                <Col xs={10} md={6}>
                  <Input
                    disabled={!editable}
                    placeholder={t("Company")}
                    value={p.company}
                    onChange={e => setParticipant(index, { company: e.target.value })}
                  />
                </Col>
                <Col xs={2} md={2}>
                  {editable && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("Remove")}
                      onClick={() =>
                        set({ participants: form.participants.filter((_, i) => i !== index) })
                      }
                    />
                  )}
                </Col>
              </Row>
            ))}
            {editable && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  set({
                    participants: [...form.participants, { name: "", role: "other", company: "" }]
                  })
                }
              >
                {t("Add participant")}
              </Button>
            )}
          </Card>

          <Card title={t("Reservations of the customer")} className="mb-3">
            <Space direction="vertical" className="wty-full">
              <Checkbox
                disabled={!editable}
                checked={form.reservations.contractPenalty}
                onChange={e =>
                  set({ reservations: { ...form.reservations, contractPenalty: e.target.checked } })
                }
              >
                {t("Contractual penalty reserved")}
              </Checkbox>
              <Checkbox
                disabled={!editable}
                checked={form.reservations.knownDefects}
                onChange={e =>
                  set({ reservations: { ...form.reservations, knownDefects: e.target.checked } })
                }
              >
                {t("Rights for the listed known defects reserved")}
              </Checkbox>
              <Input.TextArea
                disabled={!editable}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder={t("Further reservations")}
                value={form.reservations.note}
                maxLength={2000}
                onChange={e =>
                  set({ reservations: { ...form.reservations, note: e.target.value } })
                }
              />
              <Typography.Text type="secondary" className="wty-small">
                {t(
                  "Without these reservations the customer loses the penalty and the rights for known defects on acceptance."
                )}
              </Typography.Text>
            </Space>
          </Card>

          <Card title={t("Warranty")} className="mb-3">
            {acceptance.warrantyInfo ? (
              <Space direction="vertical" className="wty-full">
                {acceptance.warrantyInfo.summary.periods.map(p => (
                  <div key={p._id}>
                    {p.label || categoryLabels(t)[p.category]}: {formatDay(p.end) || "—"}{" "}
                    <Typography.Text type="secondary">({p.source || t("agreed")})</Typography.Text>
                  </div>
                ))}
                <Link to={`/user/warranty/warranties/${acceptance.warrantyInfo._id}`}>
                  {t("Open warranty")}
                </Link>
              </Space>
            ) : (
              <>
                <Typography.Paragraph type="secondary">
                  {t("The warranty starts with the acceptance. Which kinds of work does it cover?")}
                </Typography.Paragraph>
                <Checkbox.Group
                  disabled={!editable}
                  value={form.warrantyCategories}
                  onChange={value => set({ warrantyCategories: value })}
                  options={CATEGORIES.map(c => ({ value: c, label: categoryLabels(t)[c] }))}
                  className="wty-checks"
                />
              </>
            )}
          </Card>

          {acceptance.invoices?.length > 0 && (
            <Card title={t("Final invoice and retentions")} className="mb-3">
              <List
                size="small"
                dataSource={acceptance.invoices}
                renderItem={i => (
                  <List.Item>
                    <div>
                      <Typography.Text strong>{i.invoiceNumber || i.title}</Typography.Text>{" "}
                      {i.type === "final" && <Tag>{t("Final invoice")}</Tag>}
                      {i.retention && (
                        <div className="wty-small">
                          {t("Retention {{amount}}", {
                            amount: formatEuro(i.retention.amount, lng)
                          })}
                          {i.retention.dueDate
                            ? ` · ${t("due {{date}}", { date: formatDay(i.retention.dueDate) })}`
                            : ""}
                          {i.retention.guarantee ? ` · ${t("replaced by a bond")}` : ""}
                        </div>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          )}

          <Card title={t("Internal note")} className="mb-3">
            <Input.TextArea
              disabled={!editable}
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={5000}
              value={form.internalNote}
              onChange={e => set({ internalNote: e.target.value })}
            />
          </Card>

          {(customerSigned || contractorSigned) && (
            <Card title={t("Signatures")} className="mb-3">
              <Row gutter={[12, 12]}>
                {[
                  ["customer", t("Customer")],
                  ["contractor", t("Contractor")]
                ].map(([party, label]) => {
                  const s = acceptance.signatures?.[party];
                  return s ? (
                    <Col xs={24} md={12} key={party}>
                      <img className="wty-signature" src={s.image} alt="" />
                      <div className="wty-small">
                        {label}: {s.name} · {formatDateTime(s.signedAt, lng)}
                        {s.method === "REMOTE" ? ` · ${t("via link")}` : ""}
                      </div>
                    </Col>
                  ) : null;
                })}
              </Row>
            </Card>
          )}
        </Col>
      </Row>

      <SignOnSiteDialog
        open={dialog === "sign"}
        acceptance={acceptance}
        defaultEmail={acceptance.clientInfo?.email || ""}
        onCancel={() => setDialog(null)}
        onSign={async values => {
          const res = await signAcceptance(id, values).catch(error => {
            generalErrorAlert(error);
            throw error;
          });
          apply(res.data.data);
          if (values.party === "customer") {
            setDialog(null);
            Success(t("Accepted. The warranty has started."));
          }
        }}
      />
      <RecordDialog
        open={dialog === "record"}
        onCancel={() => setDialog(null)}
        onSubmit={values => run(() => recordAcceptance(id, values), t("Acceptance recorded"))}
      />
      <RefuseDialog
        open={dialog === "refuse"}
        acceptance={acceptance}
        onCancel={() => setDialog(null)}
        onSubmit={values => run(() => refuseAcceptance(id, values), t("Saved"))}
      />
      <RequestDialog
        open={dialog === "request"}
        acceptance={acceptance}
        onCancel={() => setDialog(null)}
        onSubmit={values => run(() => requestAcceptance(id, values), t("Deadline started"))}
      />
      <SimpleTextDialog
        open={dialog === "reopen"}
        title={t("Reopen")}
        label={t("Reason")}
        onCancel={() => setDialog(null)}
        onSubmit={reason => run(() => reopenAcceptance(id, { reason }), t("Reopened"))}
      />
      <SimpleTextDialog
        open={dialog === "email"}
        title={t("Email")}
        label={t("Email address")}
        type="email"
        okText={t("Send")}
        initial={acceptance.clientInfo?.email || ""}
        onCancel={() => setDialog(null)}
        onSubmit={to => run(() => emailAcceptance(id, { to }), t("Sent"))}
      />
      <SimpleTextDialog
        open={dialog === "link"}
        title={t("Signing link for the customer")}
        label={t("Send to")}
        type="email"
        okText={t("Send link")}
        initial={acceptance.clientInfo?.email || ""}
        onCancel={() => setDialog(null)}
        onSubmit={email =>
          run(() => createSignLink(id, { email, send: true }), t("Link sent")).then(res =>
            setLinkUrl(res.data.url)
          )
        }
      />
      <Modal
        rootClassName="warranty-page"
        open={Boolean(linkUrl)}
        title={t("Signing link for the customer")}
        footer={<Button onClick={() => setLinkUrl("")}>{t("Close")}</Button>}
        onCancel={() => setLinkUrl("")}
      >
        <LinkResult url={linkUrl} />
      </Modal>
      <DefectStatusDialog
        open={Boolean(defectDialog)}
        defect={defectDialog?.defect}
        status={defectDialog?.status}
        onCancel={() => setDefectDialog(null)}
        onSubmit={values =>
          run(() => updateDefect(id, defectDialog.defect._id, values), t("Saved")).then(() =>
            setDefectDialog(null)
          )
        }
      />
    </div>
  );
};

export default AcceptanceEditor;
